<template>
  <v-card>
    <v-card-title>
      {{ formTitle }}
      <v-spacer />

      <v-dialog
        v-if="isPersisted"
        width="400"
      >
        <template #activator="{ on: deleteOn, attrs: deleteAttrs }">
          <v-btn
            icon
            :ripple="false"
            v-bind="deleteAttrs"
            v-on="deleteOn"
          >
            <v-icon>
              {{ icons.mdiTrashCanOutline }}
            </v-icon>
          </v-btn>
        </template>
        <template #default="deleteConfirmationMenu">
          <v-card>
            <v-card-title>
              削除の確認
            </v-card-title>

            <v-card-text>
              削除を実行してもよろしいですか
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                :ripple="false"
                @click="onCancel(); deleteConfirmationMenu.value = false"
              >
                キャンセル
              </v-btn>
              <v-btn
                :loading="isSubmitting"
                :disabled="isSubmitting"
                :ripple="false"
                color="error"
                @click="onDelete(deleteConfirmationMenu)"
              >
                同意の上で削除
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
    </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        v-model="formValid"
      >
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center mb-4">
              <v-avatar
                size="64"
                color="secondary"
                class="mr-4"
              >
                <v-img
                  v-if="preview"
                  :src="preview"
                />
                <v-img
                  v-else-if="formData.profileImage"
                  :src="formData.profileImage"
                />
                <span
                  v-else-if="formData.name"
                  class="text-h6 font-weight-medium"
                >
                  {{ formData.name.substring(0, 3) }}
                </span>
                <v-icon
                  v-else
                  size="32"
                >
                  mdi-account
                </v-icon>
              </v-avatar>
              <v-file-input
                v-model="imageFile"
                accept="image/*"
                label="プロフィール画像"
                hide-details
                :clearable="false"
                class="flex-grow-1"
                @change="handleImageChange"
              >
                <template #append>
                  <v-btn
                    v-if="imageFile"
                    icon
                    small
                    :ripple="false"
                    @click.stop="handleImageChange(null)"
                  >
                    <v-icon>
                      {{ icons.mdiClose }}
                    </v-icon>
                  </v-btn>
                </template>
              </v-file-input>
            </div>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.name"
              label="名前"
              :rules="[v => !!v || '名前は必須です']"
              required
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              :value="userRoleText"
              disabled
              label="ロール"
            />
          </v-col>

          <v-col cols="12">
            <v-select
              v-model="formData.castGroupId"
              :items="castGroupOptions"
              label="グループ"
              clearable
              dense
            />
          </v-col>

          <!-- メールアドレス（waiter/customユーザーのみ・編集時のみ） -->
          <v-col
            v-if="isPersisted && canChangePassword"
            cols="12"
          >
            <v-text-field
              :value="formData.email"
              disabled
              label="メールアドレス"
            />
          </v-col>

          <!-- パスワード変更（admin専用・編集時のみ・staff/customユーザーのみ） -->
          <v-col
            v-if="isPersisted && isAdmin && canChangePassword"
            cols="12"
          >
            <v-divider class="mb-4" />
            <div class="text-subtitle-2 mb-2">
              パスワード変更
            </div>
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="newPassword"
                  label="新しいパスワード"
                  type="password"
                  hint="8文字以上で入力してください"
                  :rules="[v => !v || v.length >= 8 || '8文字以上で入力してください']"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="confirmPassword"
                  label="パスワード確認"
                  type="password"
                  :rules="[v => !newPassword || v === newPassword || 'パスワードが一致しません']"
                  :error-messages="passwordMismatchError"
                />
              </v-col>
            </v-row>
            <v-btn
              color="warning"
              :disabled="!canSubmitPassword"
              :loading="isUpdatingPassword"
              @click="onUpdatePassword"
            >
              パスワードを保存
            </v-btn>
          </v-col>

          <!-- タブコンテンツ -->
          <v-col cols="12">
            <v-tabs
              v-model="activeTab"
              background-color="transparent"
              color="primary"
              grow
            >
              <v-tab
                v-for="item in tabItems"
                :key="item.label"
                :value="item.label"
              >
                {{ item.label }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <v-tab-item>
                <v-card>
                  <v-row>
                    <v-col cols="12">
                      <v-combobox
                        :value="selectedFlags"
                        :items="userSystemFlags"
                        item-text="attributes.name"
                        return-object
                        label="ラベル"
                        no-data-text="利用可能なデータがありません"
                        multiple
                        chips
                        deletable-chips
                        hide-selected
                        hint="新しいラベルを作成するには、ラベル名を入力してください"
                        persistent-hint
                        class="mb-2"
                        @input="onComboBoxInput"
                      >
                        <template #selection="{ item }">
                          <v-chip
                            close
                            @click:close="removeFlag(item)"
                          >
                            {{ item.attributes && item.attributes.name }}
                            <v-icon
                              v-if="item.isNew"
                              x-small
                              class="ml-2"
                            >
                              {{ icons.mdiPlus }}
                            </v-icon>
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>

                    <v-col
                      v-for="header in customHeaders"
                      :key="header.value"
                      cols="12"
                      sm="6"
                    >
                      <v-checkbox
                        v-if="header.type === 'boolean'"
                        :label="header.text"
                        :input-value="(customFieldValues.find(field => Number(field.storeCustomFieldId) === Number(header.value)) || {}).value === 'true'"
                        @change="updateCustomFieldValue(header.value, $event ? 'true' : 'false')"
                      />
                      <v-text-field
                        v-else
                        :label="header.text"
                        :type="header.type"
                        :value="(customFieldValues.find(field => Number(field.storeCustomFieldId) === Number(header.value)) || {}).value || ''"
                        @input="updateCustomFieldValue(header.value, $event)"
                      />
                    </v-col>
                  </v-row>
                </v-card>
              </v-tab-item>

              <v-tab-item>
                <v-card>
                  <template-select
                    :aggregation-catalogs="aggregationCatalogs"
                    :form-aggregations="formData.aggregations"
                    :selected-aggregation-template="selectedAggregationTemplate"
                    @input="handleAggregationTemplateInput"
                  />
                  <aggregation-list
                    :aggregations="formData.aggregations"
                    @update:aggregations="updateAggregations"
                  />
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="blue darken-1"
        text
        :ripple="false"
        @click="onCancel"
      >
        キャンセル
      </v-btn>
      <v-btn
        color="primary"
        :loading="isSubmitting"
        :disabled="!formValid || isSubmitting"
        :ripple="false"
        @click="onSubmit"
      >
        {{ formSubmitText }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Vue from 'vue'
import {
  computed,
  onMounted,
  ref,
} from '@vue/composition-api'
import { mdiTrashCanOutline, mdiPlus, mdiClose } from '@mdi/js'
import ability from '@/plugins/acl/ability'
import UserApi from '@/api/v2/employee/user-management/User'
import CastGroupApi from '@/api/waiter/CastGroup'
import useUserFormState from '../composables/useUserFormState'
import useUserFormUI from '../composables/useUserFormUI'
import TemplateSelect from './TemplateSelect.vue'
import AggregationList from './AggregationList.vue'

export default {
  name: 'UserForm',
  components: {
    TemplateSelect,
    AggregationList,
  },
  props: {
    role: {
      type: String,
      required: true,
      default: '',
    },
    user: {
      type: Object,
      default: () => ({
        id: null,
        attributes: {
          name: '',
          role: '',
          profileImage: '',
          userSystemFlaggings: {
            data: [],
          },
          userCustomFieldValues: {
            data: [],
          },
          userAggregationTemplateId: null,
          aggregations: [],
        },
      }),
    },
    userSystemFlags: {
      type: Array,
      default: () => [],
    },
    customHeaders: {
      type: Array,
      default: () => [],
    },
    aggregationCatalogs: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: 'new',
    },
  },
  setup(props, { emit }) {
    const form = ref(null)
    const activeTab = ref(0)
    const tabItems = ref([
      { label: 'ラベル' },
      { label: '集計' },
    ])
    const formState = useUserFormState(props)
    const formUI = useUserFormUI(formState)

    // グループ選択肢
    const castGroupOptions = ref([])
    const fetchCastGroups = async () => {
      try {
        const res = await CastGroupApi.getCastGroups()
        const groups = res.data?.castGroups?.data || []
        castGroupOptions.value = groups.map(g => ({
          text: g.attributes.name,
          value: parseInt(g.id, 10),
        }))
      } catch {
        // ignore
      }
    }
    fetchCastGroups()

    // パスワード変更用
    const newPassword = ref('')
    const confirmPassword = ref('')
    const isUpdatingPassword = ref(false)

    const isAdmin = computed(() => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')

      return currentUser.role === 'admin'
    })

    // cast / waiter / custom ロールのユーザーのみパスワード変更可能
    const canChangePassword = computed(() => {
      const userRole = formState.state.form.role

      return userRole === 'cast' || userRole === 'waiter' || userRole === 'custom'
    })

    const passwordMismatchError = computed(() => {
      if (confirmPassword.value && newPassword.value !== confirmPassword.value) {
        return 'パスワードが一致しません'
      }

      return ''
    })

    const canSubmitPassword = computed(() => {
      return newPassword.value
        && newPassword.value.length >= 8
        && confirmPassword.value
        && newPassword.value === confirmPassword.value
        && !isUpdatingPassword.value
    })

    const onUpdatePassword = async () => {
      if (!canSubmitPassword.value) return

      isUpdatingPassword.value = true
      try {
        await UserApi.updatePassword(formState.state.form.id, newPassword.value)
        Vue.prototype.$toast.success('パスワードを更新しました')
        newPassword.value = ''
        confirmPassword.value = ''
      } catch (error) {
        const message = error.response?.data?.message || 'パスワードの更新に失敗しました'
        Vue.prototype.$toast.error(Array.isArray(message) ? message.join('\n') : message)
      } finally {
        isUpdatingPassword.value = false
      }
    }

    const onSubmit = async () => {
      if (!await form.value.validate() || formState.state.isSubmitting) return

      formState.state.isSubmitting = true
      const callback = () => {
        formState.state.isSubmitting = false
      }

      emit('submit', {
        id: formState.state.form.id,
        name: formState.state.form.name,
        profileImage: formState.state.form.imageFile || formState.state.form.profileImage,
        userSystemFlaggingsAttributes: formUI.getFlagAttributes(),
        userCustomFieldValuesAttributes: formUI.getCustomFieldAttributes(),
        userAggregationTemplateId: formState.state.form.userAggregationTemplateId,
        aggregations: formUI.getAggregationAttributes(),
        castGroupId: formState.state.form.castGroupId,
        callback,
      })
    }

    const selectedAggregationTemplate = computed(() => {
      return props.aggregationCatalogs.find(template => template.id === formState.state.form.userAggregationTemplateId)
    })

    const updateAggregations = aggregation => {
      // 同じ名前の集計がある場合は、その集計の_destroyフラグを切り替える
      formState.state.form.aggregations = formState.state.form.aggregations.map(item => {
        const targetAggregation = item.aggregations.find(tempAggregation => tempAggregation.type === aggregation.type && tempAggregation.ids === aggregation.ids)
        if (targetAggregation) {
          targetAggregation._destroy = !targetAggregation._destroy

          return item
        }

        return item
      })
    }

    const onDelete = dialog => {
      if (formState.state.isSubmitting) return

      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      formState.state.isSubmitting = true
      const callback = () => {
        formState.state.isSubmitting = false
      }
      emit('delete', { id: formState.state.form.id, callback })
    }

    const onCancel = () => {
      formState.initialize()
      emit('cancel')
    }

    onMounted(() => {
      formState.initialize()
    })

    const canUseNewClosing = computed(() => {
      return ability.can('manage', 'eod_closing')
    })

    return {
      form,
      formValid: formState.state.isValid,
      isSubmitting: computed(() => formState.state.isSubmitting),
      formData: formState.state.form,
      preview: computed(() => formState.state.form.preview),
      imageFile: computed({
        get: () => formState.state.form.imageFile,
        set: value => formUI.handleImageChange(value),
      }),
      formTitle: formState.formTitle,
      formSubmitText: formState.formSubmitText,
      userRoleText: formState.userRoleText,
      isPersisted: formState.isPersisted,
      isNew: formState.isNew,
      selectedFlags: computed(() => formState.state.flags),
      customFieldValues: computed(() => formState.state.customFields),
      selectedAggregationTemplate,
      handleImageChange: formUI.handleImageChange,
      onSubmit,
      onDelete,
      onCancel,
      onComboBoxInput: formUI.handleFlagInput,
      removeFlag: formUI.removeFlag,
      updateCustomFieldValue: formUI.updateCustomFieldValue,
      canUseNewClosing,
      activeTab,
      tabItems,
      handleAggregationTemplateInput: formUI.handleAggregationTemplateInput,
      updateAggregations,
      newPassword,
      confirmPassword,
      isUpdatingPassword,
      isAdmin,
      canChangePassword,
      passwordMismatchError,
      canSubmitPassword,
      onUpdatePassword,
      castGroupOptions,
      icons: {
        mdiTrashCanOutline,
        mdiPlus,
        mdiClose,
      },
    }
  },
}
</script>
